import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
// Styles
import styles from './Admin.module.scss'
// Config
import { ADMIN_API_BASE, PUBLIC_PHOTO_BASE, TICKET_STORAGE_KEY } from 'config/admin.config'
// 打包进来的照片清单（构建时就带上了分类、尺寸、体积）
import { ALBUM_MANIFEST } from 'App.constant'
import { ALBUM_CATEGORIES } from 'config/albums.config'

// ─────────────────────────────────────────────────────────────────────────────
// 相册后台（/admin）
//
// 这个页面是相册站的一个隐藏路由：不在导航里、不被任何地方链接。
// 密码由**服务器**校验（api.xhja.cn/pics/login），前端拿到的只是一张 12 小时
// 时效的票据，存在 sessionStorage 里，关掉标签页就没了。
// 前端**不包含任何密钥** —— 本站是纯静态站，写在这里的东西等于贴在公网上。
// ─────────────────────────────────────────────────────────────────────────────

interface Photo {
  category: string
  date: string
  width: number
  height: number
  file: string
  title?: string
  desc?: string
  url: string
  size: number
  missing?: boolean
}

interface StatusPayload {
  repo: string
  repo_size_kb: number
  count: number
  total_bytes: number
  categories: { id: string; name: string }[]
  max_file_mb: number
  photos: Photo[]
  // 清单里有、仓库里其实已经没有这个文件的条目（服务端会自动清掉）
  stale?: string[]
}

interface FileResult {
  name: string
  ok: boolean
  message: string
  /** 服务端最终文件名（含分类，如 `hide/2026-09-22_xxx.webp`）——上传后唯一的权威名字 */
  file?: string
  /** 对外可用的图片链接 */
  url?: string
}

class ApiError extends Error {
  status: number
  constructor(status: number, message: string) {
    super(message)
    this.status = status
  }
}

async function callApi<T>(path: string, options: { method?: string; body?: unknown; ticket?: string } = {}): Promise<T> {
  const headers: Record<string, string> = {}
  if (options.body !== undefined) headers['Content-Type'] = 'application/json'
  if (options.ticket) headers['X-Admin-Ticket'] = options.ticket
  let res: Response
  try {
    res = await fetch(ADMIN_API_BASE + path, {
      method: options.method || 'GET',
      headers,
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
    })
  } catch (e) {
    throw new ApiError(0, '连不上服务器，请检查网络')
  }
  const text = await res.text()
  let data: Record<string, unknown> = {}
  try {
    data = text ? JSON.parse(text) : {}
  } catch (e) {
    throw new ApiError(res.status, '服务器返回了无法解析的内容')
  }
  if (!res.ok) {
    const msg = (data.message as string) || (data.error as string) || `请求失败（HTTP ${res.status}）`
    throw new ApiError(res.status, String(msg))
  }
  return data as unknown as T
}

function formatBytes(bytes: number): string {
  if (!bytes) return '0 B'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`
}

function readAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const result = String(reader.result || '')
      const comma = result.indexOf(',')
      resolve(comma >= 0 ? result.slice(comma + 1) : '')
    }
    reader.onerror = () => reject(new Error('读取文件失败'))
    reader.readAsDataURL(file)
  })
}

const photoUrl = (file: string) => `${PUBLIC_PHOTO_BASE}/${file}`

// ── 缩略图（这一小段是「上传了一张图，可它就是显示不出来」的根治处）──────────────
//
// 病根有两层，都在"上传后那几十秒"里：
//   ① 上传是提交 GitHub ⇒ ESA 还要**重建**才有这张图。这段时间里那个图片地址
//      压根不存在，而 `esa.jsonc` 配了 `notFoundStrategy: singlePageApplication`
//      ⇒ 不存在的路径**返回 200 + text/html**（首页兜底页，约 1.1 KB），
//      浏览器拿到一段 HTML 去当图片解码 ⇒ **裂图，而且不报 404、控制台都不一定报错**。
//   ② 那份 HTML 响应**没有 Cache-Control**，浏览器按 Last-Modified 走启发式缓存
//      （≈ 距上次构建时间的 10%，常常十几分钟）⇒ 图早就上线了，**刷新还是裂的**，
//      看起来就像"上传没成功 / 网站坏了"。以前唯一的解法是"删掉重传"（换了个新地址）。
//
// 所以这里两件事：
//   · 刚上传的那几张用**本地预览**（blob:）渲染 —— 根本不依赖 CDN，上传完立刻可见；
//   · 其余图片加载失败时**自动换一个缓存键重试**（`?b=时间戳`）—— 正好绕开 ② 里那份
//     被缓存住的 HTML；试几次还不行就显示一句人话，而不是一个空白方块。
function PhotoThumb({ photo, preview }: { photo: Photo; preview?: string }) {
  // 0 = 还没失败过，用原始地址（正常图片只发一次请求，不破坏缓存）
  const [stamp, setStamp] = useState(0)
  const [dead, setDead] = useState(false)
  const tries = useRef(0)
  const timer = useRef<number | null>(null)

  useEffect(() => () => { if (timer.current) window.clearTimeout(timer.current) }, [])

  // 本地预览（就是刚拖进去的那个 File）：最可靠，先给它
  if (preview) {
    return <img className={styles.thumb} src={preview} alt={photo.file} />
  }

  const retry = () => {
    tries.current += 1
    if (tries.current > 3) { setDead(true); return }
    if (timer.current) window.clearTimeout(timer.current)
    timer.current = window.setTimeout(() => setStamp(Date.now()), 1200 * tries.current)
  }

  if (dead) {
    return (
      <button
        className={styles.thumbFallback}
        type="button"
        title={photo.url}
        onClick={() => {
          tries.current = 0
          setDead(false)
          setStamp(Date.now())
        }}
      >
        <span>线上还没就绪</span>
        <span className={styles.thumbFallbackHint}>ESA 重建中 · 点这里重试</span>
      </button>
    )
  }

  const src = stamp === 0 ? photo.url : `${photo.url}?b=${stamp}`
  return (
    <a href={photo.url} target="_blank" rel="noreferrer" title="在新窗口打开原图">
      <img className={styles.thumb} src={src} alt={photo.file} loading="lazy" onError={retry} />
    </a>
  )
}

// 首屏快照：直接来自**打包进产物的清单**，零网络请求。
// 站长一打开 /admin 就能看到照片列表，不必等服务器回话。
// 服务器返回后会用实时数据覆盖它（刚上传、站点还没重建时两者会不一样）。
function snapshotFromBundle(): StatusPayload {
  const photos: Photo[] = ALBUM_MANIFEST.map((p) => ({
    category: p.category,
    date: p.date,
    width: p.width,
    height: p.height,
    file: p.file,
    size: p.size || 0,
    url: photoUrl(p.file),
  }))
  return {
    repo: '',
    repo_size_kb: 0,
    count: photos.length,
    total_bytes: photos.reduce((sum, p) => sum + (p.size || 0), 0),
    categories: ALBUM_CATEGORIES.map((c) => ({ id: c.id, name: c.name })),
    max_file_mb: 8,
    photos,
  }
}

const Admin = () => {
  const [ticket, setTicket] = useState<string>(() => {
    try { return sessionStorage.getItem(TICKET_STORAGE_KEY) || '' } catch (e) { return '' }
  })
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [loggingIn, setLoggingIn] = useState(false)
  const [loginError, setLoginError] = useState('')

  // 首屏立刻有内容：直接用打包进产物的清单，零网络请求。
  // 服务器回话后用实时数据覆盖（并标记已核对）。
  const [status, setStatus] = useState<StatusPayload>(() => snapshotFromBundle())
  const [synced, setSynced] = useState(false)
  const [loadError, setLoadError] = useState('')
  const [loading, setLoading] = useState(false)

  const [category, setCategory] = useState('')
  const [results, setResults] = useState<FileResult[]>([])
  const [uploading, setUploading] = useState(false)
  const [dragging, setDragging] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // 刚上传成功的照片：仓库内相对路径 → 本地 blob 预览地址。
  // 只为了让"上传完立刻能看到图"，不依赖 ESA 重建（见 PhotoThumb 的注释）。
  // 页面一刷新这个表就空了 —— 那时 ESA 早就构建完，直接读线上图。
  const [previews, setPreviews] = useState<Record<string, string>>({})
  const previewsRef = useRef<Record<string, string>>({})
  useEffect(() => () => {
    Object.values(previewsRef.current).forEach((u) => { try { URL.revokeObjectURL(u) } catch (e) { /* 忽略 */ } })
  }, [])

  const [newCatId, setNewCatId] = useState('')
  const [newCatName, setNewCatName] = useState('')
  const [catBusy, setCatBusy] = useState(false)
  const [catMsg, setCatMsg] = useState('')
  // 分类显示名的编辑草稿（id → 正在输入的名字）。空 = 没在改，显示服务端的值。
  const [catDraft, setCatDraft] = useState<Record<string, string>>({})

  // ── 删除队列 ──────────────────────────────────────────────────────────────
  // 站长的用法是"连着点好几张删除"。以前每点一张就发一个请求、每个请求又要跑
  // 好几趟 GitHub 提交，请求之间互相踩：删掉的会跳回来、第二张开始报
  // server_error、只有第一张生效。
  // 现在：**串行排队**，一次只提交一张；界面立即把图拿掉（乐观更新）。
  // pending 既用来给按钮显示"删除中…"，也用来挡住刷新时把图顶回来。
  const pendingRef = useRef<Set<string>>(new Set())
  const [pending, setPending] = useState<string[]>([])
  const queueRef = useRef<string[]>([])
  const drainingRef = useRef(false)

  // 复制反馈（右下角小提示）
  const [toast, setToast] = useState('')
  const toastTimer = useRef<number>(0)
  const flash = useCallback((msg: string) => {
    setToast(msg)
    if (toastTimer.current) window.clearTimeout(toastTimer.current)
    toastTimer.current = window.setTimeout(() => setToast(''), 2400)
  }, [])

  // 复制到剪贴板。优先用现代 API；老浏览器/非安全上下文回退到 execCommand。
  const copyText = useCallback(async (text: string, label: string) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text)
      } else {
        const ta = document.createElement('textarea')
        ta.value = text
        ta.style.position = 'fixed'
        ta.style.opacity = '0'
        document.body.appendChild(ta)
        ta.select()
        document.execCommand('copy')
        document.body.removeChild(ta)
      }
      flash(`已复制${label}`)
    } catch (e) {
      window.prompt('浏览器已阻止自动复制，请手动复制以下内容：', text)
    }
  }, [flash])

  const clearTicket = useCallback(() => {
    try { sessionStorage.removeItem(TICKET_STORAGE_KEY) } catch (e) { /* 忽略 */ }
    // 票据没了就别再重试队列（重试只会一路 401）
    queueRef.current = []
    pendingRef.current = new Set()
    setPending([])
    setTicket('')
    setSynced(false)
    setStatus(snapshotFromBundle())
  }, [])

  const refresh = useCallback(async (tk: string) => {
    setLoading(true)
    setLoadError('')
    try {
      const data = await callApi<StatusPayload>('/status', { ticket: tk })
      // 服务器给的是权威数据；url 统一按对外前缀重算，保证「复制链接」在
      // 本地预览里也复制出线上地址。
      //
      // ⚠️ 但**正在删除**的那些要挡掉：服务器返回的可能是提交完成前的快照，
      // 直接覆盖就会让"已经点了删除的图又跳回来"。
      const hidden = data.photos.filter((p) => pendingRef.current.has(p.file))
      const visible = data.photos.filter((p) => !pendingRef.current.has(p.file))
      const hiddenBytes = hidden.reduce((sum, p) => sum + (p.size || 0), 0)
      setStatus({
        ...data,
        photos: visible.map((p) => ({ ...p, url: photoUrl(p.file) })),
        count: visible.length,
        total_bytes: Math.max(0, data.total_bytes - hiddenBytes),
      })
      setSynced(true)
      setCategory((cur) => cur || (data.categories[0] ? data.categories[0].id : ''))
    } catch (e) {
      const err = e as ApiError
      if (err.status === 401) {
        clearTicket()
        setLoginError('登录已过期，请重新登录')
      } else {
        setLoadError(err.message)
      }
    } finally {
      setLoading(false)
    }
  }, [clearTicket])

  // 首次进入：有票据就验一下，顺带把清单拉回来
  useEffect(() => {
    if (ticket) refresh(ticket)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleLogin = useCallback(async (e: React.FormEvent) => {
    e.preventDefault()
    if (!username || !password) return
    setLoggingIn(true)
    setLoginError('')
    try {
      // 用户名与口令一起提交：服务端两个都校验（各存各的 PBKDF2 派生值），
      // 错哪个都回同一句「用户名或密码不正确」，不泄露是哪一项不对。
      const data = await callApi<{ ticket: string }>('/login', {
        method: 'POST',
        body: { username, password },
      })
      try { sessionStorage.setItem(TICKET_STORAGE_KEY, data.ticket) } catch (err) { /* 忽略 */ }
      setTicket(data.ticket)
      setPassword('')
      await refresh(data.ticket)
    } catch (err) {
      const e2 = err as ApiError
      setLoginError(e2.status === 429 ? '尝试次数太多，请等 15 分钟再试' : e2.message)
    } finally {
      setLoggingIn(false)
    }
  }, [username, password, refresh])

  const handleLogout = useCallback(async () => {
    try { await callApi('/logout', { method: 'POST', ticket }) } catch (e) { /* 忽略 */ }
    clearTicket()
  }, [ticket, clearTicket])

  // ── 上传队列 ──────────────────────────────────────────────────────────────
  // 和删除是同一类问题：一拖多张、或者上一批还没传完又拖一批，会出现**两个上传
  // 循环并行跑**，每个都去提交 GitHub ⇒ 互相踩，报 server_error 且只成功一部分。
  // 现在统一进一个队列，串行上传；已开始上传时再拖进来的会追加到队尾。
  const uploadQueueRef = useRef<File[]>([])
  const uploadingRef = useRef(false)
  const [uploadRemain, setUploadRemain] = useState(0)
  const categoryRef = useRef(category)
  useEffect(() => { categoryRef.current = category }, [category])

  const uploadFiles = useCallback(async (files: File[]) => {
    if (!files.length) return
    if (!categoryRef.current) { window.alert('请先选一个分类'); return }

    uploadQueueRef.current.push(...files)
    if (uploadingRef.current) {
      setUploadRemain(uploadQueueRef.current.length)
      return
    }

    uploadingRef.current = true
    setUploading(true)
    setResults([])
    const collected: FileResult[] = []
    let authDead = false
    try {
      while (uploadQueueRef.current.length) {
        const file = uploadQueueRef.current.shift() as File
        setUploadRemain(uploadQueueRef.current.length)
        try {
          const content = await readAsBase64(file)
          const res = await callApi<{ file: string; size: number; url?: string }>('/upload', {
            method: 'POST',
            ticket,
            body: { category: categoryRef.current, filename: file.name, content },
          })
          const url = res.url || photoUrl(res.file)
          collected.push({ name: file.name, ok: true, file: res.file, url,
                           message: `→ ${res.file.split('/').pop()}（${formatBytes(res.size)}）` })
          // 本地预览：拖进来的原图先顶上，别让站长对着一个"ESA 还没发布完"的裂图发呆
          try {
            const blobUrl = URL.createObjectURL(file)
            previewsRef.current[res.file] = blobUrl
            setPreviews({ ...previewsRef.current })
          } catch (e) { /* 建不了预览不影响上传 */ }
        } catch (err) {
          const e2 = err as ApiError
          if (e2.status === 401) { authDead = true; clearTicket(); break }
          collected.push({ name: file.name, ok: false, message: e2.message })
        }
        setResults([...collected])
      }
    } finally {
      uploadingRef.current = false
      uploadQueueRef.current = []
      setUploadRemain(0)
      setUploading(false)
      // 传完只拉一次状态（逐张拉会让列表在中途反复重建）
      if (!authDead && ticket) await refresh(ticket)
    }
  }, [ticket, refresh, clearTicket])

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setDragging(false)
    uploadFiles(Array.from(e.dataTransfer.files || []))
  }, [uploadFiles])

  // 删除时**先把这张从界面上拿掉**，再去提交 GitHub。
  // 提交要跑好几个 GitHub 往返（国内出去一趟几百毫秒），等它返回再刷新
  // 会让人觉得"点了没反应"。
  const removeLocally = useCallback((file: string) => {
    setStatus((cur) => {
      const gone = cur.photos.find((p) => p.file === file)
      const photos = cur.photos.filter((p) => p.file !== file)
      return { ...cur, photos, count: photos.length, total_bytes: cur.total_bytes - (gone ? gone.size : 0) }
    })
  }, [])

  const markPending = useCallback((file: string, on: boolean) => {
    if (on) pendingRef.current.add(file)
    else pendingRef.current.delete(file)
    setPending(Array.from(pendingRef.current))
  }, [])

  // 连续点删除时的处理：
  // · 界面上立刻隐掉（乐观更新）
  // · 请求**排队串行**提交，一次只动一张 —— 并发提交会互相覆盖（第二张起报错）
  // · 队列跑空后拉一次状态做最终确认，**这次刷新仍在 pending 保护期内**，
  //   所以服务器万一返回了提交前的旧快照，也不会把已删的图顶回来
  const handleRemove = useCallback(async (photo: Photo) => {
    if (pendingRef.current.has(photo.file)) return
    if (!window.confirm(`确定从相册里删除这张？\n\n${photo.file}\n\n（会从 GitHub 仓库移除，ESA 重建后链接失效）`)) return

    markPending(photo.file, true)
    removeLocally(photo.file)
    queueRef.current.push(photo.file)

    // 已有队列在跑：只入队，交给它一起处理（不会产生第二个并发请求）
    if (drainingRef.current) {
      flash(`已加入删除队列（还有 ${queueRef.current.length} 张在等）`)
      return
    }

    drainingRef.current = true
    let done = 0
    const failed: string[] = []
    let authDead = false
    try {
      for (;;) {
        while (queueRef.current.length) {
          const file = queueRef.current.shift() as string
          try {
            await callApi('/delete', { method: 'POST', ticket, body: { file } })
            done += 1
          } catch (err) {
            const e2 = err as ApiError
            if (e2.status === 401) { authDead = true; failed.push(file); break }
            // 404 = 仓库里本来就没有 → 目的已达成，不算失败
            if (e2.status === 404) { done += 1; continue }
            failed.push(file)
            // 真失败的要立刻解除保护，下一次刷新才会把它放回列表
            markPending(file, false)
          }
        }
        if (authDead) break
        if (ticket) await refresh(ticket)
        // 刷新期间站长可能又点了删除 → 再绕一圈；没人点就收工
        if (!queueRef.current.length) break
      }
    } finally {
      drainingRef.current = false
      queueRef.current = []
      pendingRef.current = new Set()
      setPending([])
      if (done) flash(done > 1 ? `已删除 ${done} 张` : '已删除')
      if (failed.length && !authDead) {
        window.alert(`有 ${failed.length} 张没删掉，已放回列表：\n\n${failed.join('\n')}`)
      }
    }
  }, [ticket, refresh, removeLocally, markPending, flash])

  // ── 分类管理（新增 / 改名 / 删除）────────────────────────────────────────
  // 分类写在仓库的 src/config/albums.config.json 里，由服务器改成一次 git 提交，
  // 前端只负责把动作发出去、把结果说出来。
  // ⚠️ **id 不给改**：它同时是照片所在目录名，改了所有链接都会断。
  //    所以这里只开放「显示名」改名，删分类也只在分类为空时才允许。
  const catPhotos = useMemo(() => {
    const m: Record<string, number> = {}
    status.photos.forEach((p) => { m[p.category] = (m[p.category] || 0) + 1 })
    return m
  }, [status.photos])

  const sendCategory = useCallback(async (
    body: Record<string, unknown>, label: string,
  ) => {
    setCatBusy(true)
    setCatMsg('')
    try {
      const res = await callApi<{ note?: string }>('/category', { method: 'POST', ticket, body })
      setCatMsg(`${label}成功${res.note ? '：' + res.note : ''}`)
      if (ticket) await refresh(ticket)
      return true
    } catch (err) {
      setCatMsg(`${label}失败：${(err as ApiError).message}`)
      return false
    } finally {
      setCatBusy(false)
    }
  }, [ticket, refresh])

  const handleAddCategory = useCallback(async (e: React.FormEvent) => {
    e.preventDefault()
    const id = newCatId.trim().toLowerCase()
    const name = newCatName.trim()
    if (!id || !name) return
    if (await sendCategory({ action: 'add', id, name }, `新增分类「${name}」`)) {
      setNewCatId('')
      setNewCatName('')
    }
  }, [newCatId, newCatName, sendCategory])

  const handleRenameCategory = useCallback(async (id: string, draft: string) => {
    const name = draft.trim()
    if (!name) return
    if (await sendCategory({ action: 'rename', id, name }, `把分类「${id}」改名为「${name}」`)) {
      setCatDraft((cur) => {
        const next = { ...cur }
        delete next[id]
        return next
      })
    }
  }, [sendCategory])

  const handleDeleteCategory = useCallback(async (id: string, name: string, count: number) => {
    // 还有照片的分区不给删（服务端也会拒，这里先拦一道，省一次往返）
    if (count > 0) {
      window.alert(`「${name}」下还有 ${count} 张照片，删不掉。\n\n`
        + '请先在下方照片列表中删除这些照片（或转移至其他分类），再删除该分类。')
      return
    }
    if (!window.confirm(`确定删除分类「${name}」（${id}）？\n\n`
      + '仅删除该分类本身，不影响任何照片；重建后该分类将从导航栏移除。')) return
    await sendCategory({ action: 'delete', id, name }, `删除分类「${name}」`)
  }, [sendCategory])

  const grouped = useMemo(() => {
    const order = status.categories.map((c) => c.id)
    return order
      .map((id) => ({
        id,
        name: status.categories.find((c) => c.id === id)?.name || id,
        photos: status.photos.filter((p) => p.category === id),
      }))
      .filter((g) => g.photos.length > 0)
  }, [status])

  // ── 未登录 ──
  if (!ticket) {
    return (
      <div className={styles.page}>
        <header className={styles.header}>
          <a className={styles.back} href="/">← 回相册</a>
          <h1 className={styles.title}>相册后台</h1>
        </header>
        <form className={styles.loginBox} onSubmit={handleLogin} autoComplete="off">
          {/* 刻意不给这两个框任何"值提示"：没有默认值、没有 placeholder、没有 name 语义。
              autoComplete 全部关掉 —— 原来用户名框用 autoComplete="username"，
              浏览器（Chrome / 密码管理器）会据此把**保存过的账号名自动填进去**，
              于是登录页上就"明文显示"了用户名，等于自己把门槛抹掉。 */}
          <div className={styles.field}>
            <label className={styles.label} htmlFor="user">用户名</label>
            <input
              id="user"
              className={styles.input}
              type="text"
              name="admin-field-1"
              autoComplete="off"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              data-form-type="other"
              data-lpignore="true"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoFocus
            />
          </div>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="pwd">密码</label>
            <input
              id="pwd"
              className={styles.input}
              type="password"
              name="admin-field-2"
              autoComplete="new-password"
              data-form-type="other"
              data-lpignore="true"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
          <button className={styles.primary} type="submit" disabled={loggingIn || !username || !password}>
            {loggingIn ? '登录中…' : '登录'}
          </button>
          {loginError ? <p className={styles.error}>{loginError}</p> : null}
        </form>
      </div>
    )
  }

  // ── 已登录 ──
  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <a className={styles.back} href="/">← 回相册</a>
        <h1 className={styles.title}>相册后台</h1>
        <button className={styles.ghost} type="button" onClick={handleLogout}>退出登录</button>
      </header>

      <div className={styles.stats}>
        <span className={styles.stat}><b>{status.count}</b> 张照片</span>
        <span className={styles.stat}><b>{formatBytes(status.total_bytes)}</b> 图片体积</span>
        <span className={styles.stat}><b>{status.categories.length}</b> 个分类</span>
        {pending.length > 0 ? (
          <span className={styles.statBusy}>正在删除 {pending.length} 张…</span>
        ) : null}
        {uploading ? (
          <span className={styles.statBusy}>
            正在上传{uploadRemain > 0 ? `（还有 ${uploadRemain} 张排队）` : ''}…
          </span>
        ) : null}
        {synced ? (
          <span className={styles.statMuted} title="GitHub 的仓库体积统计有延迟，仅供参考">
            仓库 {status.repo} · {status.repo_size_kb} KB
          </span>
        ) : (
          <span className={styles.statMuted}>正在与服务器核对…</span>
        )}
        <button className={styles.ghost} type="button" onClick={() => refresh(ticket)} disabled={loading || pending.length > 0}>
          {loading ? '刷新中…' : '刷新'}
        </button>
      </div>

      {loadError ? <p className={styles.error}>{loadError}</p> : null}

      <section className={styles.card}>
        <h2 className={styles.cardTitle}>上传照片</h2>
        <div className={styles.row}>
          <label className={styles.label} htmlFor="cat">放到哪个分类</label>
          <select id="cat" className={styles.select} value={category} onChange={(e) => setCategory(e.target.value)}>
            {status.categories.map((c) => (
              <option key={c.id} value={c.id}>{c.name}（{c.id}）</option>
            ))}
          </select>
        </div>
        <div
          className={`${styles.dropzone}${dragging ? ` ${styles.dropzoneActive}` : ''}`}
          onDragOver={(e) => { e.preventDefault(); setDragging(true) }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <p className={styles.dropTitle}>
            {uploading
              ? (uploadRemain > 0 ? `上传中…（还有 ${uploadRemain} 张排队）` : '上传中…')
              : '把照片拖到这里，或点击选择'}
          </p>
          <p className={styles.dropHint}>
            支持 jpg / png / webp，单张最大 {status.max_file_mb || 8}MB；可一次选多张。
            图片原样上传、不做压缩，画质不掉。上传中也可以继续拖，会自动排队。
          </p>
          <input
            ref={fileInputRef}
            className={styles.fileInput}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            multiple
            onChange={(e) => {
              uploadFiles(Array.from(e.target.files || []))
              e.target.value = ''
            }}
          />
        </div>
        {results.length ? (
          <ul className={styles.results}>
            {results.map((r, i) => (
              <li key={i} className={r.ok ? styles.resultOk : styles.resultBad}>
                <span className={styles.resultName}>{r.name}</span>
                <span>{r.message}</span>
                {r.ok && r.url ? (
                  <button className={styles.action} type="button" onClick={() => copyText(r.url as string, '图片链接')}>
                    复制链接
                  </button>
                ) : null}
                {r.ok && r.url ? <span className={styles.resultUrl}>{r.url}</span> : null}
              </li>
            ))}
          </ul>
        ) : null}
        {results.some((r) => r.ok) ? (
          <p className={styles.hint}>
            <strong>上面的文件名是服务端最终的名字</strong>（会加日期前缀、按文件真实格式定扩展名）。
            取链接一律用右边那个「复制链接」，别照着自己电脑上的文件名手打 ——
            打错了不会报 404，只会显示成空白。
            <br />
            ESA 重建要几十秒~几分钟。这段时间里下面的列表用的是你本机的原图做预览，
            所以<strong>看得见不代表线上已经能打开</strong>；重建完成后再刷新一次就是线上图了。
          </p>
        ) : null}
      </section>

      <section className={styles.card}>
        <h2 className={styles.cardTitle}>分类管理</h2>
        <p className={styles.hint}>
          分类就是相册顶部那排「全部 / 旅行 / 日常 …」按钮。改动会提交到仓库，
          ESA 重建后生效（几十秒~几分钟）。
          <br />
          分类 id 不可修改 —— 它同时是照片所在的文件夹名，改了链接就全断；
          想换名字改「显示名」即可。分类下还有照片时删不掉，先把照片删掉再删分类。
        </p>

        {status.categories.map((c) => {
          const count = catPhotos[c.id] || 0
          const draft = catDraft[c.id] !== undefined ? catDraft[c.id] : c.name
          const dirty = draft.trim().length > 0 && draft.trim() !== c.name
          return (
            <div key={c.id} className={styles.catRow}>
              <input
                className={`${styles.input} ${styles.catName}`}
                value={draft}
                maxLength={12}
                aria-label={`「${c.name}」的显示名`}
                onChange={(e) => {
                  // 先把值取出来再进函数式更新：不要把事件对象留到 updater 里读
                  const next = e.target.value
                  setCatDraft((cur) => ({ ...cur, [c.id]: next }))
                }}
              />
              <span className={styles.catId}>{c.id}</span>
              <span className={styles.catCount}>{count} 张</span>
              <button
                className={styles.ghost}
                type="button"
                disabled={catBusy || !dirty}
                onClick={() => handleRenameCategory(c.id, draft)}
              >
                保存改名
              </button>
              <button
                className={`${styles.actionDanger} ${styles.catDanger}`}
                type="button"
                disabled={catBusy}
                onClick={() => handleDeleteCategory(c.id, c.name, count)}
              >
                删除分类
              </button>
            </div>
          )
        })}
        {status.categories.length === 0 ? (
          <p className={styles.hint}>还没有分类，用下面这行加一个。</p>
        ) : null}

        <form className={styles.row} onSubmit={handleAddCategory} style={{ marginTop: 16 }}>
          <input
            className={styles.input}
            placeholder="英文 id，如 friends"
            value={newCatId}
            onChange={(e) => setNewCatId(e.target.value)}
          />
          <input
            className={styles.input}
            placeholder="显示名，如 朋友"
            value={newCatName}
            onChange={(e) => setNewCatName(e.target.value)}
          />
          <button className={styles.primary} type="submit" disabled={catBusy || !newCatId || !newCatName}>
            {catBusy ? '提交中…' : '新增分类'}
          </button>
        </form>
        <p className={styles.hint}>id 只能用小写英文、数字、短横线；它同时也是照片文件夹名。</p>
        {catMsg ? <p className={styles.hint}>{catMsg}</p> : null}
      </section>

      <section className={styles.card}>
        <h2 className={styles.cardTitle}>现有照片 · 取链接</h2>
        <p className={styles.hint}>
          点击「复制链接」即可获取该图片网址，粘贴到博客中即可。链接长这样：
          <code className={styles.code}>https://pics.xhja.cn/photos/&lt;分类&gt;/&lt;文件名&gt;</code>
          <br />
          点缩略图可以在新窗口里看看原图长什么样。
        </p>
        {grouped.length === 0 ? (
          <p className={styles.hint}>还没有照片。用上面的「上传照片」加第一张吧。</p>
        ) : null}
        {grouped.map((group) => (
          <div key={group.id} className={styles.group}>
            <div className={styles.groupHead}>
              <h3 className={styles.groupTitle}>{group.name} · {group.photos.length} 张</h3>
              <button
                className={styles.ghost}
                type="button"
                onClick={() => copyText(
                  group.photos.map((p) => p.url).join('\n'),
                  `「${group.name}」全部 ${group.photos.length} 条链接`,
                )}
              >
                复制本分类全部链接
              </button>
            </div>
            <div className={styles.grid}>
              {group.photos.map((photo) => (
                <figure key={photo.file} className={styles.item}>
                  <PhotoThumb photo={photo} preview={previews[photo.file]} />
                  <figcaption className={styles.meta}>
                    <span className={styles.filename} title={photo.file}>{photo.file.split('/').pop()}</span>
                    <span className={styles.dim}>
                      {photo.width}×{photo.height} · {formatBytes(photo.size)}
                      {photo.missing ? ' · ⚠ 仓库里找不到' : ''}
                    </span>
                  </figcaption>
                  <div className={styles.actions}>
                    <button
                      className={styles.action}
                      type="button"
                      onClick={() => copyText(photo.url, '图片链接')}
                    >
                      复制链接
                    </button>
                    <button
                      className={styles.actionDanger}
                      type="button"
                      disabled={pending.indexOf(photo.file) >= 0}
                      onClick={() => handleRemove(photo)}
                    >
                      {pending.indexOf(photo.file) >= 0 ? '删除中…' : '删除'}
                    </button>
                  </div>
                </figure>
              ))}
            </div>
          </div>
        ))}
      </section>

      {toast ? <div className={styles.toast}>{toast}</div> : null}
    </div>
  )
}

export default Admin
